import type { ChatMessage, ToolCallEntry } from '../types/chat';

function findLatestAssistantMessageIndex(messages: ChatMessage[], runId: string): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role === 'assistant' && message.runId === runId) {
      return i;
    }
  }

  return -1;
}

function buildRunningToolCall(
  message: ChatMessage,
  toolCall: Pick<ToolCallEntry, 'name' | 'arg'> & Partial<Pick<ToolCallEntry, 'contentOffset'>>,
): ToolCallEntry {
  return {
    name: toolCall.name,
    arg: toolCall.arg,
    status: 'running' as const,
    contentOffset: toolCall.contentOffset ?? message.content.length,
  };
}

export function appendToolCallToMessages(
  messages: ChatMessage[],
  runId: string,
  toolCall: Pick<ToolCallEntry, 'name' | 'arg'> & Partial<Pick<ToolCallEntry, 'contentOffset'>>,
  createMessage: () => ChatMessage,
): ChatMessage[] {
  const messageIndex = findLatestAssistantMessageIndex(messages, runId);

  if (messageIndex >= 0) {
    const updated = [...messages];
    const message = updated[messageIndex];
    updated[messageIndex] = {
      ...message,
      toolCalls: [...(message.toolCalls ?? []), buildRunningToolCall(message, toolCall)],
    };
    return updated;
  }

  const message = createMessage();
  return [
    ...messages,
    {
      ...message,
      toolCalls: [buildRunningToolCall(message, toolCall)],
    },
  ];
}

export function markLatestToolCallDone(
  messages: ChatMessage[],
  runId: string,
  toolName?: string,
): ChatMessage[] {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message.role !== 'assistant' || message.runId !== runId || !message.toolCalls?.length) {
      continue;
    }

    for (let toolIndex = message.toolCalls.length - 1; toolIndex >= 0; toolIndex -= 1) {
      const toolCall = message.toolCalls[toolIndex];
      if (toolCall.status !== 'running') continue;
      if (toolName && toolCall.name !== toolName) continue;

      const updated = [...messages];
      updated[messageIndex] = {
        ...message,
        toolCalls: message.toolCalls.map((entry, index) => (
          index === toolIndex ? { ...entry, status: 'done' as const } : entry
        )),
      };
      return updated;
    }
  }

  return messages;
}

/**
 * Update the arg of the most recent running tool call for a given runId.
 * Used to show tool progress from phase:"update" events.
 */
export function updateToolCallProgress(
  messages: ChatMessage[],
  runId: string,
  toolName: string,
  progressArg: string,
): ChatMessage[] {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message.role !== 'assistant' || message.runId !== runId || !message.toolCalls?.length) {
      continue;
    }

    for (let toolIndex = message.toolCalls.length - 1; toolIndex >= 0; toolIndex -= 1) {
      const toolCall = message.toolCalls[toolIndex];
      if (toolCall.status !== 'running') continue;
      if (toolCall.name !== toolName) continue;

      const updated = [...messages];
      updated[messageIndex] = {
        ...message,
        toolCalls: message.toolCalls.map((entry, index) => (
          index === toolIndex ? { ...entry, arg: progressArg } : entry
        )),
      };
      return updated;
    }
  }

  return messages;
}

export function markAllToolCallsDone(messages: ChatMessage[], runId: string): ChatMessage[] {
  let changed = false;

  const updated = messages.map((message) => {
    if (message.role !== 'assistant' || message.runId !== runId || !message.toolCalls?.length) {
      return message;
    }

    const hasRunningTool = message.toolCalls.some((toolCall) => toolCall.status === 'running');
    if (!hasRunningTool) {
      return message;
    }

    changed = true;
    return {
      ...message,
      toolCalls: message.toolCalls.map((toolCall) => (
        toolCall.status === 'running' ? { ...toolCall, status: 'done' as const } : toolCall
      )),
    };
  });

  return changed ? updated : messages;
}
